import last from 'lodash/last';
import get from 'lodash/get';

// possible statuses from backend
export const statuses = {
  BLOCKED_HOLIDAY: 'BLOCKED_HOLIDAY',
  BLOCKED_OTHER: 'BLOCKED_OTHER',
  BLOCKED_NO_SCHOOL: 'BLOCKED_NO_SCHOOL',
  BLOCKED_SCHOOL_EVENT: 'BLOCKED_SCHOOL_EVENT',
  BLOCKED_OUTSIDE_AVAILABILITY_WINDOW: 'BLOCKED_OUTSIDE_AVAILABILITY_WINDOW',
  PARTIALLY_OPEN: 'PARTIALLY_OPEN',
  OPEN: 'OPEN',
  FULL: 'FULL',
  DELETED: 'DELETED',
};

export const DAYS_TO_HIDE = [statuses.FULL];
export const DAYS_BLOCKED = [
  statuses.BLOCKED_HOLIDAY,
  statuses.BLOCKED_OTHER,
  statuses.BLOCKED_NO_SCHOOL,
  statuses.BLOCKED_SCHOOL_EVENT,
  statuses.BLOCKED_OUTSIDE_AVAILABILITY_WINDOW,
];
export const OPEN_DAYS = [statuses.OPEN, statuses.PARTIALLY_OPEN];
export const APPOINTMENTS_TO_HIDE = [statuses.FULL, statuses.DELETED];

export function getDateFromDateString(dateString) {
  const pieces = dateString.split('-');
  // add leading 0 to month and day for UTC
  pieces[1] = pieces[1].length === 2 ? pieces[1] : `0${pieces[1]}`;
  pieces[2] = pieces[2].length === 2 ? pieces[2] : `0${pieces[2]}`;
  // add time to prevent issue with incorrect date parsing. '2017-10-10' becomes 2017-10-09 19:00:00
  return new Date(`${pieces.join('-')}T12:00:00`);
}

export function canRSVP(day, rsvpDeadline) {
  const diffMS = getDateFromDateString(day.date).getTime() - Date.now();
  const diffDay = diffMS / (1000 * 3600 * 24); // 1 day in ms
  return diffDay > rsvpDeadline;
}

export function outsideAvailabilityWindow(firstVisitDay, lastVisitDay) {
  const firstVisitDayMS = getDateFromDateString(firstVisitDay).getTime();
  const lastVisitDayMS = getDateFromDateString(lastVisitDay).getTime();
  return (dateString) => {
    const dayMS = getDateFromDateString(dateString).getTime();
    return dayMS < firstVisitDayMS || dayMS > lastVisitDayMS;
  };
}

// returns true for appointments that aren't full and aren't already booked by the college
export function appointmentsFilter(appointment) {
  return !APPOINTMENTS_TO_HIDE.includes(appointment.status) && !appointment.bookedByCollege;
}

// returns null for blocked days and OPEN days with no availability
export const dayOrNull = (availabilityVisible, rsvpDeadline) => (day) => {
  // day is null or full or empty, school hasn't published availability, or rsvp deadline has passed
  if (
    !day ||
    !availabilityVisible ||
    DAYS_TO_HIDE.includes(day.status) ||
    (day.status !== statuses.BLOCKED_OUTSIDE_AVAILABILITY_WINDOW && !canRSVP(day, rsvpDeadline)) ||
    (OPEN_DAYS.includes(day.status) && day.appointments.length === 0)
  ) {
    return null;
  }
  return day;
};

// returns an array with nulls for days that aren't populated from backend
// and places them in the correct position according to the week
export function orderDays(week, days, outsideAvailability) {
  return week.map((colDate) => {
    if (outsideAvailability(colDate)) {
      return { date: colDate, status: statuses.BLOCKED_OUTSIDE_AVAILABILITY_WINDOW };
    }
    return days.find(({ date }) => date === colDate) || null;
  });
}

// copies the data from each day into a format usable for the table
export const copyDayInfo = (day) => {
  const res = {};
  // full days or days with no slots
  if (day === null) {
    res.status = 'EMPTY';
    return res;
  }

  res.status = day.status;

  if (!DAYS_BLOCKED.includes(day.status)) {
    res.id = day.id;
    res.appointments = day.appointments.filter(appointmentsFilter);
  }

  return res;
};

// combines consecutive days of the same status
export function mergeLikeDays(res, day) {
  // day status matches the status of the day before it, and it's not an open day
  if (res.length && day.status === last(res).status && !OPEN_DAYS.includes(day.status)) {
    last(res).span = get(last(res), 'span', 1) + 1; // some fancy incrementing
  } else {
    res.push(day);
  }
  return res;
}

// puts availability dates in order matching weekdays
export function arrangeAppointmentsByDay(week, repVisitsAvailability, school) {
  const availabilityVisible = get(school, 'repVisitsAccount.availabilityVisible', false);
  const firstVisitDay = get(school, 'repVisitsAccount.firstVisitDay');
  const lastVisitDay = get(school, 'repVisitsAccount.lastVisitDay');
  const rsvpDeadline = get(school, 'repVisitsAccount.availabilitySettings.rsvpDeadline');

  return orderDays(week, repVisitsAvailability, outsideAvailabilityWindow(firstVisitDay, lastVisitDay))
    .map(dayOrNull(availabilityVisible, rsvpDeadline))
    .map(copyDayInfo)
    .reduce(mergeLikeDays, []);
}
